import React, { useEffect, useMemo, useState } from 'react';

type ConsentChoice = 'reject' | 'analytics' | 'all';

const STORAGE_KEY = 'sh_cookie_consent_v1';

const consentPayloadByChoice: Record<ConsentChoice, Record<string, 'granted' | 'denied'>> = {
  reject: {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  },
  analytics: {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  },
  all: {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  },
};

const applyConsent = (choice: ConsentChoice) => {
  const payload = consentPayloadByChoice[choice];
  const w = window as Window & { gtag?: (...args: any[]) => void };
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', payload);
  }
};

const CookieConsentBanner: React.FC = () => {
  const [ready, setReady] = useState(false);
  const [savedChoice, setSavedChoice] = useState<ConsentChoice | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'reject' || raw === 'analytics' || raw === 'all') {
      setSavedChoice(raw);
      applyConsent(raw);
    }
    setReady(true);
  }, []);

  const isVisible = useMemo(() => ready && !savedChoice, [ready, savedChoice]);

  const handleChoice = (choice: ConsentChoice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    setSavedChoice(choice);
    applyConsent(choice);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[1200] p-3 sm:p-4">
      <div className="mx-auto max-w-4xl rounded-xl border border-white/15 bg-[#0d0d0d]/90 backdrop-blur-xl shadow-2xl p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <p className="text-white text-sm font-semibold mb-1">Cookie settings</p>
            <p className="text-white/75 text-xs sm:text-sm">
              We use analytics cookies to understand visits, time on page, and navigation paths. You can change this later in browser storage settings.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => handleChoice('reject')}
              className="btn btn-outline text-sm w-full sm:w-auto"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={() => handleChoice('analytics')}
              className="btn btn-outline text-sm w-full sm:w-auto"
            >
              Analytics only
            </button>
            <button
              type="button"
              onClick={() => handleChoice('all')}
              className="btn btn-gold text-sm w-full sm:w-auto"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
